import logger from '@app/utils/logger';
import { useState, useEffect } from 'react';
import { useChatContext } from 'stream-chat-react';

// Create constants to avoid magic strings
const CHANNEL_TYPE = 'messaging';
const DM_COHORT_PREFIX = 'dm_cohort_';
const NEW_MESSAGE_EVENT = 'message.new';

export function useUnreadCounts(
  teamChannelId: string,
  cohortId: number
): {
  teamChatCount: number;
  dmCounts: Record<string, number>;
} {
  const { client } = useChatContext();
  const [teamChatCount, setTeamChatCount] = useState(0);
  const [dmCounts, setDmCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!client || !teamChannelId) return;
    let isMounted = true;

    async function loadDmCounts() {
      if (!client.userID || !isMounted) return;

      const filters = {
        type: CHANNEL_TYPE,
        customType: `${DM_COHORT_PREFIX}${cohortId}`, // filter for DMs in this cohort
        members: { $in: [client.userID] },
      };
      const channels = await client.queryChannels(filters);

      const map: Record<string, number> = {};
      channels.forEach((ch) => {
        const unread = ch.state.read[client.userID!].unread_messages;

        // only pick members that actually have a user object
        const other = Object.values(ch.state.members).find((m) => m.user !== undefined && m.user.id !== client.userID);

        if (other && other.user) {
          map[other.user.id] = unread;
        }
      });

      setDmCounts(map);
    }

    async function loadTeamCount() {
      if (!client.userID || !isMounted) return;

      // Query the specific channel without watching it (watching sets everything as read)
      const filter = { type: CHANNEL_TYPE, id: teamChannelId };
      const channels = await client.queryChannels(filter, {}, { state: true });

      if (channels.length > 0) {
        const channel = channels[0];
        const unreadCount = channel.state.read[client.userID].unread_messages;
        setTeamChatCount(unreadCount);
      }
    }

    loadTeamCount();
    loadDmCounts();

    // listen for all new messages
    const handler = (event: any) => {
      loadTeamCount();
      loadDmCounts();
    };
    client.on(NEW_MESSAGE_EVENT, handler);

    return () => {
      isMounted = false;
      client.off(NEW_MESSAGE_EVENT, handler);
    };
  }, [client, teamChannelId, cohortId]);

  return { teamChatCount, dmCounts };
}
